import { SearchIcon, ArrowLeftIcon } from 'lucide-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import AuthButtons from '~/src/components/AuthButtons'
import DesktopNav from '~/src/components/DesktopNav'
import LibraryNavButton from '~/src/components/LibraryNavButton'
import useHeaderSheet from '~/src/hooks/useHeaderSheet'
import DupeLogo from '~/src/components/svg/DupeLogo'
import { useChatGPTBackLink } from '~/src/hooks/useChatGPTBackLink'
import { cn } from '~/src/lib/utils'
import { useNavClickTracker } from '~/src/hooks/useNavClickTracker'

// The header renders on every page; keep framer-motion (pulled in by
// RewardsHeaderTrigger) out of its initial chunk.
const RewardsHeaderTrigger = dynamic(
  () => import('~/src/components/RewardsHeaderTrigger'),
  {
    ssr: false,
  }
)

const HeaderSheetMenu = dynamic(
  () => import('~/src/components/HeaderSheetMenu'),
  {
    ssr: false,
  }
)

const UserInitials = dynamic(() => import('~/src/components/UserInitials'), {
  ssr: false,
})

/**
 * Collapsing the header removes up to ~28px of height (md:py-3 → py-1.5, min-h 64 → 48),
 * and scroll anchoring compensates by reducing scrollY by that same amount. The gap between
 * these two thresholds must therefore stay comfortably wider than that, or the collapse
 * itself pushes scrollY back under the expand threshold and the state oscillates.
 */
const SCROLL_COMPACT_AFTER_Y = 64
const SCROLL_EXPAND_BELOW_Y = 8

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  expand?: boolean
  showPoints?: boolean
  sticky?: boolean
  iframe?: boolean
  clearBg?: boolean
  LogoComponent?: React.ReactNode
  inputClassName?: string
  /**
   * Hides the right-hand action cluster (New Search, auth buttons, user
   * initials, points). For pages outside the consumer shopping flow — e.g.
   * /partners — where those affordances are noise.
   */
  hideActions?: boolean
  /**
   * Renders the desktop primary nav (Obsessions / Steals / App) beside the
   * wordmark, so the bar persists across the pages it links to. Opt-in —
   * see `DesktopNav`.
   */
  showDesktopNav?: boolean
}

const LayoutHeader: React.FC<Props> = ({
  expand,
  showPoints,
  sticky,
  iframe,
  clearBg,
  LogoComponent,
  className,
  hideActions,
  showDesktopNav,
}) => {
  const [shouldExpand, setShouldExpand] = useState(expand)
  const { sheetProps, libraryNav } = useHeaderSheet()
  const trackNavClick = useNavClickTracker('header')
  const [scrolled, setScrolled] = useState(false)
  const chatGPTBackLink = useChatGPTBackLink()

  // Scroll + hysteresis (no short wrapper around the sticky header — that breaks sticky).
  // rAF coalesces wheel sub-steps; overflow-anchor:none on the header reduces scroll jumps when height changes.
  useEffect(() => {
    if (!clearBg) return
    let raf = 0
    const onScroll = () => {
      if (raf !== 0) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = window.scrollY
        setScrolled((prev) =>
          prev ? y > SCROLL_EXPAND_BELOW_Y : y > SCROLL_COMPACT_AFTER_Y
        )
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [clearBg])

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (iframe) {
      e.currentTarget.setAttribute('target', '_blank')
    }
  }

  useEffect(() => {
    if (shouldExpand) return

    if (expand) {
      setShouldExpand(true)
    }
  }, [expand, shouldExpand])

  return (
    <header
      className={cn(
        'relative flex flex-col items-center gap-2 px-3 py-1 md:py-3 transition-all duration-300',
        clearBg && '[overflow-anchor:none]',
        className,
        {
          'md:sticky md:top-0 md:z-50': sticky,
          'bg-white dark:bg-black': !clearBg,
          'border-b-transparent bg-transparent dark:bg-transparent':
            clearBg && !scrolled,
          'bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-black/5 dark:border-white/10 !py-1.5':
            clearBg && scrolled,
        }
      )}
    >
      <div
        className={cn(
          'mx-auto flex w-full items-center justify-between gap-2 transition-all duration-300 md:gap-4',
          clearBg && scrolled ? 'min-h-[48px]' : 'min-h-[64px]'
        )}
        onClick={trackNavClick}
      >
        <div className='flex items-center md:pl-2'>
          <div className='flex items-center gap-2 md:gap-4'>
            <HeaderSheetMenu {...sheetProps} />
            <div
              className={cn(
                'relative flex items-center justify-start gap-2',
                // Reserved slot, dropped when the nav is shown so the links sit
                // the spec'd 36px off the wordmark rather than ~225px.
                { 'md:min-w-[225px]': !showDesktopNav }
              )}
            >
              <Link href='/' onClick={handleOnClick} aria-label='Dupe home'>
                <DupeLogo
                  className={cn(
                    'h-7 w-auto transition-all duration-300',
                    clearBg && scrolled ? 'md:h-6' : 'md:h-8'
                  )}
                />
              </Link>
              {LogoComponent}
            </div>
          </div>
          {showDesktopNav && <DesktopNav className='md:ml-9' />}
        </div>
        {!hideActions && (
          <div className='flex items-center justify-end md:order-3 md:min-w-[225px]'>
            <div className='flex items-center gap-2 md:pr-2'>
              {/* The Library nav drops the points pill: one less thing up top. */}
              {showPoints && !libraryNav.enabled && <RewardsHeaderTrigger />}
              {/* ChatGPT Back Link - Desktop only */}
              {chatGPTBackLink && (
                <a
                  href={chatGPTBackLink}
                  className={cn(
                    'animate-fade-in',
                    'z-50 hidden items-center justify-center gap-2 md:flex',
                    'w-auto rounded-full border border-black/30 px-3 font-semibold backdrop-blur-md transition-all duration-300 hover:bg-black hover:text-white dark:bg-white/5',
                    clearBg && scrolled ? 'h-8 text-xs' : 'h-10 text-sm'
                  )}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <ArrowLeftIcon className='h-4 w-4' />
                  <span>Back to ChatGPT</span>
                </a>
              )}
              <div className='inline-flex animate-fade-in'>
                <Link
                  href='/'
                  onClick={handleOnClick}
                  aria-label='New search'
                  className={cn(
                    'z-50 flex items-center justify-center md:gap-2',
                    'rounded-full border border-black/30 font-semibold backdrop-blur-md transition-all duration-300 hover:bg-black hover:text-white md:w-auto md:pl-3 md:pr-4 dark:bg-white/5',
                    clearBg && scrolled
                      ? 'h-10 w-10 text-sm md:h-9 md:text-xs'
                      : 'h-10 w-10 text-sm'
                  )}
                >
                  <SearchIcon
                    className={cn(
                      'transition-all duration-300',
                      clearBg && scrolled
                        ? 'h-4 w-4 md:h-3.5 md:w-3.5'
                        : 'h-4 w-4'
                    )}
                  />
                  <span className='hidden md:block'>New Search</span>
                </Link>
              </div>
              <AuthButtons
                className={cn(
                  'h-10 text-sm transition-all duration-300',
                  clearBg && scrolled && 'md:h-9 md:text-xs'
                )}
              />
              {/* Beside the avatar: it's their own stuff, like the account. */}
              {libraryNav.show && (
                <LibraryNavButton
                  count={libraryNav.count}
                  lists={libraryNav}
                  onOpen={libraryNav.onOpen}
                  className='ml-1 mr-2.5 md:ml-2 md:mr-3'
                />
              )}
              <UserInitials
                className={cn(
                  'transition-all duration-300',
                  clearBg ? (scrolled ? 'md:size-8' : 'md:size-10') : ''
                )}
              />
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default LayoutHeader
